"use client"

import * as React from 'react'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { Button } from '@open-mercato/ui/primitives/button'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { CIM_STATUS_CLASS } from '../../../lib/status'

type WaybillData = {
  id: string
  waybillNumber: string
  waybillType: string
  status: string
  consignorName: string
  consigneeName: string
  carrierName: string
  forwardingAgentName: string | null
  goodsDescription: string
  grossWeightKg: number | null
  nhmCode: string | null
  hsCode: string | null
  isDangerous: boolean
  ridClass: string | null
  ridUnNumber: string | null
  freightCharges: number | null
  freightCurrency: string
  chargesPaidBy: string
  remarks: string | null
  pdfUrl: string | null
  issuedAt: string | null
  deliveredAt: string | null
  createdAt: string
  updatedAt: string
}

type WaybillsResponse = {
  items: Array<Record<string, unknown>>
  total: number
}

function str(item: Record<string, unknown>, camel: string, snake: string): string {
  if (typeof item[camel] === 'string') return item[camel] as string
  if (typeof item[snake] === 'string') return item[snake] as string
  return ''
}

function strOrNull(item: Record<string, unknown>, camel: string, snake: string): string | null {
  if (typeof item[camel] === 'string') return item[camel] as string
  if (typeof item[snake] === 'string') return item[snake] as string
  return null
}

function numOrNull(item: Record<string, unknown>, camel: string, snake: string): number | null {
  if (typeof item[camel] === 'number') return item[camel] as number
  if (typeof item[snake] === 'number') return item[snake] as number
  return null
}

function mapWaybillData(item: Record<string, unknown>): WaybillData | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    waybillNumber: str(item, 'waybillNumber', 'waybill_number'),
    waybillType: str(item, 'waybillType', 'waybill_type') || 'CIM',
    status: typeof item.status === 'string' ? item.status : 'DRAFT',
    consignorName: str(item, 'consignorName', 'consignor_name'),
    consigneeName: str(item, 'consigneeName', 'consignee_name'),
    carrierName: str(item, 'carrierName', 'carrier_name'),
    forwardingAgentName: strOrNull(item, 'forwardingAgentName', 'forwarding_agent_name'),
    goodsDescription: str(item, 'goodsDescription', 'goods_description'),
    grossWeightKg: numOrNull(item, 'grossWeightKg', 'gross_weight_kg'),
    nhmCode: strOrNull(item, 'nhmCode', 'nhm_code'),
    hsCode: strOrNull(item, 'hsCode', 'hs_code'),
    isDangerous: typeof item.isDangerous === 'boolean' ? item.isDangerous : (typeof item.is_dangerous === 'boolean' ? item.is_dangerous : false),
    ridClass: strOrNull(item, 'ridClass', 'rid_class'),
    ridUnNumber: strOrNull(item, 'ridUnNumber', 'rid_un_number'),
    freightCharges: numOrNull(item, 'freightCharges', 'freight_charges'),
    freightCurrency: str(item, 'freightCurrency', 'freight_currency') || 'PLN',
    chargesPaidBy: str(item, 'chargesPaidBy', 'charges_paid_by') || 'CONSIGNOR',
    remarks: strOrNull(item, 'remarks', 'remarks'),
    pdfUrl: strOrNull(item, 'pdfUrl', 'pdf_url'),
    issuedAt: strOrNull(item, 'issuedAt', 'issued_at'),
    deliveredAt: strOrNull(item, 'deliveredAt', 'delivered_at'),
    createdAt: str(item, 'createdAt', 'created_at'),
    updatedAt: str(item, 'updatedAt', 'updated_at'),
  }
}


export default function CimWaybillDetailPage({ params }: { params?: { id?: string } }) {
  const id = typeof params?.id === 'string' ? params.id : ''
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'cim-waybill-detail',
  })

  const [waybill, setWaybill] = React.useState<WaybillData | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [issuing, setIssuing] = React.useState(false)
  const [signing, setSigning] = React.useState(false)
  const [delivering, setDelivering] = React.useState(false)
  const [generatingPdf, setGeneratingPdf] = React.useState(false)
  const [reloadToken, setReloadToken] = React.useState(0)

  const handleRefresh = React.useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  React.useEffect(() => {
    if (!id) {
      setWaybill(null)
      setIsLoading(false)
      setLoadError(t('cim.detail.error.notFound', 'CIM waybill not found'))
      return
    }
    let cancelled = false

    async function loadWaybill() {
      setIsLoading(true)
      setLoadError(null)
      try {
        const fallback: WaybillsResponse = { items: [], total: 0 }
        const call = await apiCall<WaybillsResponse>(
          `/api/cim/waybills?ids=${encodeURIComponent(id)}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('cim.waybills.error.load', 'Failed to load CIM waybills')
          if (!cancelled) setLoadError(message)
          return
        }
        const payload = call.result ?? fallback
        const items = Array.isArray(payload.items) ? payload.items : []
        const first = items[0]
        if (!cancelled) {
          setWaybill(first ? mapWaybillData(first) : null)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('cim.waybills.error.load', 'Failed to load CIM waybills')
          setLoadError(message)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadWaybill()
    return () => { cancelled = true }
  }, [id, reloadToken, scopeVersion, t])

  const handleIssue = React.useCallback(async () => {
    if (!id || !waybill) return
    const confirmed = await confirm({
      title: t('cim.waybills.issueConfirm', 'Issue waybill {{waybillNumber}}?', { waybillNumber: waybill.waybillNumber }),
      variant: 'default',
    })
    if (!confirmed) return
    setIssuing(true)
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          '/api/cim/waybills/issue',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ id }),
          },
          { errorMessage: t('cim.waybills.error.issue', 'Failed to issue waybill') },
        ),
        context: {},
      })
      flash(t('cim.waybills.flash.issued', 'CIM waybill issued'), 'success')
      handleRefresh()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('cim.waybills.error.issue', 'Failed to issue waybill')
      flash(message, 'error')
    } finally {
      setIssuing(false)
    }
  }, [id, waybill, confirm, handleRefresh, t])

  const handleSign = React.useCallback(async () => {
    if (!id || !waybill) return
    const confirmed = await confirm({
      title: t('cim.waybills.signConfirm', 'Sign waybill {{waybillNumber}}?', { waybillNumber: waybill.waybillNumber }),
      variant: 'default',
    })
    if (!confirmed) return
    setSigning(true)
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          '/api/cim/waybills/sign',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ id, role: 'carrier', signatureType: 'QUALIFIED' }),
          },
          { errorMessage: t('cim.waybills.error.sign', 'Failed to sign waybill') },
        ),
        context: {},
      })
      flash(t('cim.waybills.flash.signed', 'CIM waybill signed'), 'success')
      handleRefresh()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('cim.waybills.error.sign', 'Failed to sign waybill')
      flash(message, 'error')
    } finally {
      setSigning(false)
    }
  }, [id, waybill, confirm, handleRefresh, t])

  const handleDeliver = React.useCallback(async () => {
    if (!id || !waybill) return
    const confirmed = await confirm({
      title: t('cim.waybills.deliverConfirm', 'Mark waybill {{waybillNumber}} as delivered?', { waybillNumber: waybill.waybillNumber }),
      variant: 'default',
    })
    if (!confirmed) return
    setDelivering(true)
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          '/api/cim/waybills/deliver',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ id }),
          },
          { errorMessage: t('cim.waybills.error.deliver', 'Failed to mark as delivered') },
        ),
        context: {},
      })
      flash(t('cim.waybills.flash.delivered', 'CIM waybill marked as delivered'), 'success')
      handleRefresh()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('cim.waybills.error.deliver', 'Failed to mark as delivered')
      flash(message, 'error')
    } finally {
      setDelivering(false)
    }
  }, [id, waybill, confirm, handleRefresh, t])

  const handleGeneratePdf = React.useCallback(async () => {
    if (!id) return
    setGeneratingPdf(true)
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          '/api/cim/waybills/pdf',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ id }),
          },
          { errorMessage: t('cim.waybills.error.pdf', 'Failed to generate PDF') },
        ),
        context: {},
      })
      flash(t('cim.waybills.flash.pdfGenerated', 'PDF generated successfully'), 'success')
      handleRefresh()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('cim.waybills.error.pdf', 'Failed to generate PDF')
      flash(message, 'error')
    } finally {
      setGeneratingPdf(false)
    }
  }, [id, handleRefresh, t])

  const noValue = <span className="text-muted-foreground text-sm">—</span>

  return (
    <Page>
      <PageBody>
        {isLoading && <LoadingMessage label="" />}
        {loadError && <ErrorMessage label={loadError} />}
        {!isLoading && !loadError && !waybill && (
          <ErrorMessage label={t('cim.detail.error.notFound', 'CIM waybill not found')} />
        )}
        {!isLoading && !loadError && waybill && (
          <>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="font-mono text-xl font-bold">{waybill.waybillNumber}</h2>
                <p className="text-sm text-muted-foreground">{waybill.waybillType}</p>
                <span className={CIM_STATUS_CLASS[waybill.status] ?? ''}>
                  {t(`cim.waybills.status.${waybill.status}`, waybill.status)}
                </span>
              </div>
              <div className="flex gap-2">
                {waybill.status === 'DRAFT' && (
                  <Button
                    type="button"
                    onClick={() => { void handleIssue() }}
                    disabled={issuing}
                  >
                    {issuing
                      ? t('cim.waybills.issuing', 'Issuing...')
                      : t('cim.waybills.actions.issue', 'Issue')}
                  </Button>
                )}
                {waybill.status === 'ISSUED' && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { void handleSign() }}
                    disabled={signing}
                  >
                    {signing
                      ? t('cim.waybills.signing', 'Signing...')
                      : t('cim.waybills.actions.sign', 'Sign')}
                  </Button>
                )}
                {(waybill.status === 'SIGNED' || waybill.status === 'IN_TRANSIT') && (
                  <Button
                    type="button"
                    onClick={() => { void handleDeliver() }}
                    disabled={delivering}
                  >
                    {delivering
                      ? t('cim.waybills.delivering', 'Updating...')
                      : t('cim.waybills.actions.deliver', 'Mark Delivered')}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { void handleGeneratePdf() }}
                  disabled={generatingPdf}
                >
                  {generatingPdf
                    ? t('cim.waybills.generatingPdf', 'Generating...')
                    : t('cim.waybills.actions.generatePdf', 'Generate PDF')}
                </Button>
                {waybill.pdfUrl && (
                  <Button type="button" variant="outline" asChild>
                    <a href={waybill.pdfUrl} target="_blank" rel="noreferrer">
                      {t('cim.waybills.actions.downloadPdf', 'Download PDF')}
                    </a>
                  </Button>
                )}
                <Button type="button" variant="outline" asChild>
                  <Link href="/backend/cim-waybills">
                    {t('cim.waybills.actions.back', 'Back')}
                  </Link>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="rounded border p-4">
                <h3 className="mb-3 font-semibold">{t('cim.groups.parties', 'Parties')}</h3>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-muted-foreground">{t('cim.fields.consignorName', 'Consignor Name')}</dt>
                    <dd className="font-medium">{waybill.consignorName || noValue}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t('cim.fields.consigneeName', 'Consignee Name')}</dt>
                    <dd className="font-medium">{waybill.consigneeName || noValue}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t('cim.fields.carrierName', 'Carrier Name')}</dt>
                    <dd className="font-medium">{waybill.carrierName || noValue}</dd>
                  </div>
                  {waybill.forwardingAgentName && (
                    <div>
                      <dt className="text-muted-foreground">{t('cim.fields.forwardingAgentName', 'Forwarding Agent')}</dt>
                      <dd className="font-medium">{waybill.forwardingAgentName}</dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="rounded border p-4">
                <h3 className="mb-3 font-semibold">{t('cim.groups.goods', 'Goods Description')}</h3>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-muted-foreground">{t('cim.fields.goodsDescription', 'Goods Description')}</dt>
                    <dd className="font-medium">{waybill.goodsDescription || noValue}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t('cim.fields.grossWeightKg', 'Gross Weight (kg)')}</dt>
                    <dd className="font-medium">{waybill.grossWeightKg !== null ? `${waybill.grossWeightKg} kg` : noValue}</dd>
                  </div>
                  {waybill.nhmCode && (
                    <div>
                      <dt className="text-muted-foreground">{t('cim.fields.nhmCode', 'NHM Code')}</dt>
                      <dd className="font-mono text-xs">{waybill.nhmCode}</dd>
                    </div>
                  )}
                  {waybill.hsCode && (
                    <div>
                      <dt className="text-muted-foreground">{t('cim.fields.hsCode', 'HS Code')}</dt>
                      <dd className="font-mono text-xs">{waybill.hsCode}</dd>
                    </div>
                  )}
                  {waybill.isDangerous && (
                    <div>
                      <dt className="text-muted-foreground">{t('cim.fields.isDangerous', 'Dangerous Goods')}</dt>
                      <dd className="font-medium text-orange-600">
                        {t('cim.waybills.dangerousYes', 'Yes — RID applies')}
                        {waybill.ridClass && ` (Class ${waybill.ridClass})`}
                        {waybill.ridUnNumber && `, ${waybill.ridUnNumber}`}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="rounded border p-4">
                <h3 className="mb-3 font-semibold">{t('cim.groups.financial', 'Financial')}</h3>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-muted-foreground">{t('cim.fields.freightCharges', 'Freight Charges')}</dt>
                    <dd className="font-medium">
                      {waybill.freightCharges !== null
                        ? `${waybill.freightCharges} ${waybill.freightCurrency}`
                        : noValue}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{t('cim.fields.chargesPaidBy', 'Charges Paid By')}</dt>
                    <dd className="font-medium">{waybill.chargesPaidBy || noValue}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded border p-4">
                <h3 className="mb-3 font-semibold">{t('cim.groups.other', 'Other')}</h3>
                <dl className="space-y-2 text-sm">
                  {waybill.remarks && (
                    <div>
                      <dt className="text-muted-foreground">{t('cim.fields.remarks', 'Remarks')}</dt>
                      <dd className="font-medium">{waybill.remarks}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-muted-foreground">{t('cim.waybills.columns.createdAt', 'Created')}</dt>
                    <dd className="text-xs text-muted-foreground">
                      {waybill.createdAt ? new Date(waybill.createdAt).toLocaleString() : noValue}
                    </dd>
                  </div>
                  {waybill.issuedAt && (
                    <div>
                      <dt className="text-muted-foreground">{t('cim.waybills.issuedAt', 'Issued At')}</dt>
                      <dd className="text-xs text-muted-foreground">
                        {new Date(waybill.issuedAt).toLocaleString()}
                      </dd>
                    </div>
                  )}
                  {waybill.deliveredAt && (
                    <div>
                      <dt className="text-muted-foreground">{t('cim.waybills.deliveredAt', 'Delivered At')}</dt>
                      <dd className="text-xs text-muted-foreground">
                        {new Date(waybill.deliveredAt).toLocaleString()}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </>
        )}
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
